import type BpmnModeler from 'bpmn-js/lib/Modeler'
import type NavigatedViewer from 'bpmn-js/lib/NavigatedViewer'
import type { TFunction } from 'i18next'
import { useEffect, useRef } from 'react'
import type { IssueType } from '@/components/analysis/summary/types/triage'
import {
	SPOT_DIM_CLASS,
	SPOT_HOVERED_CLASS,
	SPOT_KEEP_CLASS,
	SPOT_RELATED_CLASS,
	SPOT_SELECTED_CLASS,
	SPOT_TYPE_CLASSES,
	SPOTLIGHT_MARKER_CLASSES,
	styleForType,
} from '@/components/bpmn/improvements/improvementTypeStyles'
import { summaryIdFromBpmnXmlId } from '@/components/bpmn/utils/summaryIdFromBpmnId'
import { useTranslation } from '@/hooks/useTranslation'

const TAG_OVERLAY_TYPE = 'spotlight-tag'
const FIT_PADDING_PX = 80

type BpmnViewer = BpmnModeler | InstanceType<typeof NavigatedViewer>

interface ShapeLike {
	id: string
	type: string
	x?: number
	y?: number
	width?: number
	height?: number
	waypoints?: unknown
	source?: { id: string }
	target?: { id: string }
	labelTarget?: { id: string; type: string }
}

interface OverlaysService {
	add: (
		elementId: string,
		type: string,
		config: {
			position: { top?: number; right?: number; bottom?: number; left?: number }
			html: HTMLElement
		}
	) => string
	remove: (filter: { type: string }) => void
}

interface CanvasService {
	addMarker: (elementId: string, marker: string) => void
	removeMarker: (elementId: string, marker: string) => void
	getContainer: () => HTMLElement
	viewbox: () => {
		x: number
		y: number
		width: number
		height: number
		scale: number
		outer: { width: number; height: number }
	}
	zoom: (newScale?: number | string) => number
	scrollToElement: (el: unknown | unknown[], padding?: number) => void
}

interface ElementRegistryService {
	filter: (fn: (el: ShapeLike) => boolean) => ShapeLike[]
	get: (id: string) => ShapeLike | undefined
}

interface EventBusService {
	on: (event: string, callback: (event: { element?: ShapeLike }) => void) => void
	off: (event: string, callback: (event: { element?: ShapeLike }) => void) => void
}

export interface SpotlightApply {
	primaryBpmnId: string
	relatedBpmnIds: readonly string[]
	type: IssueType
	numberMap: Map<string, string>
}

export function isBpmnElementOffScreen(modeler: BpmnViewer, bpmnId: string): boolean {
	try {
		const registry = modeler.get('elementRegistry') as ElementRegistryService
		const canvas = modeler.get('canvas') as CanvasService
		const el = registry.get(bpmnId)
		if (!el || el.x == null || el.y == null) return true
		const vb = canvas.viewbox()
		const width = el.width ?? 0
		const height = el.height ?? 0
		return (
			el.x + width < vb.x || el.y + height < vb.y || el.x > vb.x + vb.width || el.y > vb.y + vb.height
		)
	} catch {
		return true
	}
}

function isLaneOrPoolType(type: string): boolean {
	return /Participant|Lane|Collaboration/.test(type)
}

function highlightedIds(apply: SpotlightApply): string[] {
	return [apply.primaryBpmnId, ...apply.relatedBpmnIds.filter((id) => id !== apply.primaryBpmnId)]
}

function stepNumberFor(bpmnId: string, numberMap: Map<string, string>): string {
	const summaryId = summaryIdFromBpmnXmlId(bpmnId)
	return (summaryId && numberMap.get(summaryId)) || bpmnId
}

function clearMarkers(canvas: CanvasService, ids: Iterable<string>) {
	for (const id of ids) {
		for (const marker of SPOTLIGHT_MARKER_CLASSES) {
			try {
				canvas.removeMarker(id, marker)
			} catch {
				// Element may have been deleted.
			}
		}
	}
}

function markOverlayKeeps(container: HTMLElement, ids: Iterable<string>) {
	// bpmn-js private overlay root (`.djs-overlays[data-container-id]`). It is not
	// part of the public overlays API, so a bpmn-js upgrade can break dimming.
	// The animation-frame pass repeats the mark after bpmn-js inserts the nodes.
	for (const node of container.querySelectorAll('.djs-overlays.mm-spot-keep')) {
		node.classList.remove(SPOT_KEEP_CLASS)
	}
	for (const id of ids) {
		const overlayRoot = container.querySelector(
			`.djs-overlays[data-container-id="${CSS.escape(id)}"]`
		)
		overlayRoot?.classList.add(SPOT_KEEP_CLASS)
	}
}

function fitHighlighted(canvas: CanvasService, registry: ElementRegistryService, ids: string[]) {
	const shapes = ids
		.map((id) => registry.get(id))
		.filter((el): el is ShapeLike => Boolean(el && el.x != null && el.y != null))
	if (shapes.length === 0) return

	let minX = Infinity
	let minY = Infinity
	let maxX = -Infinity
	let maxY = -Infinity
	for (const el of shapes) {
		const x = el.x ?? 0
		const y = el.y ?? 0
		const width = el.width ?? 0
		const height = el.height ?? 0
		minX = Math.min(minX, x)
		minY = Math.min(minY, y)
		maxX = Math.max(maxX, x + width)
		maxY = Math.max(maxY, y + height)
	}

	const vb = canvas.viewbox()
	const pad = FIT_PADDING_PX / Math.max(vb.scale, 0.001)
	const outside = shapes.some((el) => {
		const x = el.x ?? 0
		const y = el.y ?? 0
		const width = el.width ?? 0
		const height = el.height ?? 0
		return x < vb.x || y < vb.y || x + width > vb.x + vb.width || y + height > vb.y + vb.height
	})
	if (!outside) return

	const neededW = maxX - minX + pad * 2
	const neededH = maxY - minY + pad * 2
	const scaleToFit = Math.min(
		vb.outer.width / Math.max(neededW, 1),
		vb.outer.height / Math.max(neededH, 1)
	)
	const nextScale = Math.min(vb.scale, scaleToFit)
	if (nextScale < vb.scale - 0.001) {
		canvas.zoom(nextScale)
	}
	canvas.scrollToElement(shapes, FIT_PADDING_PX)
}

function tagText(t: TFunction, n: string, kind: 'selected' | 'related' | 'hovered'): string {
	if (kind === 'selected') return t('components.bpmnEditor.improvements.tagSelected', { n })
	if (kind === 'hovered') return t('components.bpmnEditor.improvements.tagHovered', { n })
	return t('components.bpmnEditor.improvements.relatedStep', { n })
}

function applyHoveredState(
	canvas: CanvasService,
	registry: ElementRegistryService,
	apply: SpotlightApply,
	hoveredId: string | null,
	tags: Map<string, HTMLElement>,
	markedIds: Set<string>,
	t: TFunction
) {
	const relatedSet = new Set(apply.relatedBpmnIds)
	for (const id of apply.relatedBpmnIds) {
		try {
			canvas.removeMarker(id, SPOT_HOVERED_CLASS)
		} catch {
			// Element may have been deleted.
		}
	}
	if (hoveredId && relatedSet.has(hoveredId) && registry.get(hoveredId)) {
		try {
			canvas.addMarker(hoveredId, SPOT_HOVERED_CLASS)
			markedIds.add(hoveredId)
		} catch {
			// Hover target gone.
		}
	}
	for (const [id, tag] of tags) {
		const selected = id === apply.primaryBpmnId
		const hovered = !selected && hoveredId === id
		const n = stepNumberFor(id, apply.numberMap)
		tag.className = `mm-spot-tag${selected ? ' mm-spot-tag--selected' : ''}${hovered ? ' mm-spot-tag--hovered' : ''}`
		tag.textContent = tagText(t, n, selected ? 'selected' : hovered ? 'hovered' : 'related')
	}
}

export function useSpotlight(
	modeler: BpmnViewer | null,
	xmlLoaded: boolean,
	apply: SpotlightApply | null,
	hoveredBpmnId: string | null,
	onShapeHover: (bpmnId: string | null) => void
): { isActive: boolean } {
	const { t } = useTranslation()
	const markedIdsRef = useRef<Set<string>>(new Set())
	const tagNodesRef = useRef<Map<string, HTMLElement>>(new Map())
	const onShapeHoverRef = useRef(onShapeHover)
	onShapeHoverRef.current = onShapeHover
	const hoveredRef = useRef(hoveredBpmnId)
	hoveredRef.current = hoveredBpmnId
	const applyKey = apply
		? `${apply.type}:${apply.primaryBpmnId}:${apply.relatedBpmnIds.join(',')}`
		: ''
	const fitKeyRef = useRef<string>('')

	useEffect(() => {
		if (!modeler || !xmlLoaded) return

		const overlays = modeler.get('overlays') as OverlaysService
		const canvas = modeler.get('canvas') as CanvasService
		const registry = modeler.get('elementRegistry') as ElementRegistryService
		const eventBus = modeler.get('eventBus') as EventBusService
		const container = canvas.getContainer()

		function clearAll() {
			overlays.remove({ type: TAG_OVERLAY_TYPE })
			clearMarkers(canvas, markedIdsRef.current)
			markedIdsRef.current = new Set()
			tagNodesRef.current = new Map()
			container.classList.remove(SPOT_DIM_CLASS, ...SPOT_TYPE_CLASSES)
			for (const node of container.querySelectorAll('.djs-overlays.mm-spot-keep')) {
				node.classList.remove(SPOT_KEEP_CLASS)
			}
		}

		function paint(next: SpotlightApply) {
			clearAll()
			const ids = highlightedIds(next)
			const style = styleForType(next.type)
			container.classList.add(SPOT_DIM_CLASS, style.spotTypeClass)

			const highlightSet = new Set(ids)
			const keep = new Set<string>(ids)
			for (const id of ids) {
				keep.add(`${id}_label`)
			}
			for (const el of registry.filter((entry) => isLaneOrPoolType(entry.type))) {
				keep.add(el.id)
			}
			for (const el of registry.filter(
				(entry) =>
					entry.type === 'label' && entry.labelTarget != null && isLaneOrPoolType(entry.labelTarget.type)
			)) {
				keep.add(el.id)
			}
			for (const el of registry.filter(
				(entry) =>
					Boolean(entry.waypoints) &&
					entry.source != null &&
					entry.target != null &&
					highlightSet.has(entry.source.id) &&
					highlightSet.has(entry.target.id)
			)) {
				keep.add(el.id)
			}

			for (const id of keep) {
				if (!registry.get(id)) continue
				try {
					canvas.addMarker(id, SPOT_KEEP_CLASS)
					markedIdsRef.current.add(id)
				} catch {
					// Element may have been deleted.
				}
			}

			for (const id of ids) {
				if (!registry.get(id)) continue
				const selected = id === next.primaryBpmnId
				try {
					canvas.addMarker(id, selected ? SPOT_SELECTED_CLASS : SPOT_RELATED_CLASS)
					canvas.addMarker(id, style.spotTypeClass)
					markedIdsRef.current.add(id)
				} catch {
					continue
				}

				const n = stepNumberFor(id, next.numberMap)
				const tag = document.createElement('div')
				tag.className = selected ? 'mm-spot-tag mm-spot-tag--selected' : 'mm-spot-tag'
				tag.textContent = tagText(t, n, selected ? 'selected' : 'related')
				tag.setAttribute('aria-hidden', 'true')
				const shape = registry.get(id)
				overlays.add(id, TAG_OVERLAY_TYPE, {
					position: { top: -6, left: (shape?.width ?? 0) / 2 },
					html: tag,
				})
				tagNodesRef.current.set(id, tag)
			}

			applyHoveredState(
				canvas,
				registry,
				next,
				hoveredRef.current,
				tagNodesRef.current,
				markedIdsRef.current,
				t
			)
			markOverlayKeeps(container, ids)
			window.requestAnimationFrame(() => markOverlayKeeps(container, ids))

			if (fitKeyRef.current !== applyKey) {
				fitKeyRef.current = applyKey
				fitHighlighted(canvas, registry, ids)
			}
		}

		if (apply) {
			paint(apply)
		} else {
			fitKeyRef.current = ''
			clearAll()
		}

		const onHover = (event: { element?: ShapeLike }) => {
			const id = event.element?.id
			if (!apply || !id) return
			if (id === apply.primaryBpmnId || apply.relatedBpmnIds.includes(id)) {
				onShapeHoverRef.current(id)
			}
		}
		const onOut = (event: { element?: ShapeLike }) => {
			const id = event.element?.id
			if (!id || !apply) return
			if (id === apply.primaryBpmnId || apply.relatedBpmnIds.includes(id)) {
				onShapeHoverRef.current(null)
			}
		}
		const onChanged = () => {
			if (apply) paint(apply)
			else clearAll()
		}

		eventBus.on('element.hover', onHover)
		eventBus.on('element.out', onOut)
		eventBus.on('commandStack.changed', onChanged)

		return () => {
			eventBus.off('element.hover', onHover)
			eventBus.off('element.out', onOut)
			eventBus.off('commandStack.changed', onChanged)
			clearAll()
		}
	}, [modeler, xmlLoaded, apply, applyKey, t])

	useEffect(() => {
		if (!modeler || !xmlLoaded || !apply) return
		const canvas = modeler.get('canvas') as CanvasService
		const registry = modeler.get('elementRegistry') as ElementRegistryService
		applyHoveredState(
			canvas,
			registry,
			apply,
			hoveredBpmnId,
			tagNodesRef.current,
			markedIdsRef.current,
			t
		)
	}, [apply, hoveredBpmnId, modeler, t, xmlLoaded])

	return { isActive: apply != null }
}
